import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CheckCircle, ArrowLeft, User, Mail, Phone, Home, Music } from "lucide-react";
import { formatDate } from "../utils/dateFormat";

const BookingConfirmationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const booking = location.state?.booking;

  if (!booking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg mb-4">No booking data found</p>
          <button
            onClick={() => navigate('/')}
            className="brutal-btn-primary"
          >
            GO HOME
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <button
          onClick={() => navigate('/karaoke')}
          className="mb-6 flex items-center gap-2 text-sm font-bold hover:underline"
          data-testid="back-home-btn"
        >
          <ArrowLeft size={16} />
          BACK TO CALENDAR
        </button>

        <div className="brutal-card text-center" data-testid="confirmation-card">
          <div className="w-20 h-20 mx-auto mb-6 bg-success border-4 border-black flex items-center justify-center">
            <CheckCircle className="w-12 h-12 text-white" strokeWidth={3} />
          </div>

          <h1 className="text-4xl font-syne font-bold mb-4">BOOKING RECEIVED!</h1>
          <p className="text-lg mb-8">Your karaoke room booking has been submitted</p>

          <div className="bg-amber-600 border-4 border-black p-8 mb-8">
            <p className="text-sm font-bold mb-2 text-amber-100">YOUR BOOKING REFERENCE</p>
            <p className="text-6xl font-mono font-bold text-white notranslate" translate="no" data-testid="booking-code">{booking.booking_code}</p>
          </div>

          {/* Booking Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 text-left">
            <div>
              <p className="text-sm font-bold text-muted-foreground mb-1">DATE</p>
              <p className="font-mono text-xl font-bold">{formatDate(booking.date)}</p>
            </div>
            <div>
              <p className="text-sm font-bold text-muted-foreground mb-1">TIME SLOT</p>
              <p className="font-mono text-xl font-bold">
                {booking.slot_type === 'afternoon' ? '1:00 PM - 5:00 PM' : '6:00 PM - 10:00 PM'}
              </p>
            </div>
            <div>
              <p className="text-sm font-bold text-muted-foreground mb-1">PRICE</p>
              <p className="font-mono text-xl font-bold">${booking.price}</p>
            </div>
            <div>
              <p className="text-sm font-bold text-muted-foreground mb-1">STATUS</p>
              <p className="text-xl font-bold text-destructive">🔴 PENDING PAYMENT</p>
            </div>
          </div>
          
          {/* Resident Details */}
          <div className="bg-purple-50 border-2 border-purple-300 p-4 mb-6 text-left rounded">
            <h3 className="font-bold text-purple-800 mb-3 flex items-center gap-2">
              <User size={18} /> Your Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2">
                <User size={14} className="text-purple-600" />
                <span className="font-bold">Name:</span>
                <span className="notranslate">{booking.member_name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail size={14} className="text-purple-600" />
                <span className="font-bold">Email:</span>
                <span>{booking.member_email}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone size={14} className="text-purple-600" />
                <span className="font-bold">Mobile:</span>
                <span>{booking.member_phone}</span>
              </div>
              <div className="flex items-center gap-2">
                <Home size={14} className="text-purple-600" />
                <span className="font-bold">Address:</span>
                <span>{booking.block_unit}</span>
              </div>
              {booking.kj_name && (
                <div className="flex items-center gap-2 md:col-span-2">
                  <Music size={14} className="text-purple-600" />
                  <span className="font-bold">Preferred KJ:</span>
                  <span className="notranslate">{booking.kj_name === 'Pending' ? 'To be assigned' : booking.kj_name}</span>
                </div>
              )}
            </div>
          </div>

          {/* Payment Instructions Block */}
          <div className="bg-secondary border-4 border-black p-6 mb-6 text-left">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">💳</span>
              <h3 className="text-xl font-bold">Payment Instructions</h3>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between border-b border-black/20 pb-2">
                <span className="font-bold">Method:</span>
                <span>onePA (Online Payment)</span>
              </div>
              <div className="flex justify-between border-b border-black/20 pb-2">
                <span className="font-bold">Reference:</span>
                <span className="font-mono font-bold text-lg notranslate" translate="no">{booking.booking_code}</span>
              </div>
            </div>
            
            <div className="bg-amber-600 border-2 border-black p-4 rounded mt-4">
              <p className="font-bold text-white text-center">
                ⚠️ Reference: <span className="font-mono text-xl bg-white text-amber-700 px-2 py-1 notranslate" translate="no">{booking.booking_code}</span>
              </p>
            </div>
          </div>

          {/* Payment Verification Instruction */}
          <div className="bg-amber-50 border-4 border-amber-500 p-6 mb-6 text-left">
            <p className="font-bold text-amber-800 text-lg mb-3">
              📱 An Admin or RC Manager will contact you via WhatsApp to provide the payment link or QR code.
            </p>
            <p className="font-bold text-amber-700 mb-3">
              All payments/transactions must be made via onePA only.
            </p>
            <p className="text-sm text-amber-700">
              * Your booking status will be updated to &quot;Confirmed&quot; once the Admin verifies the onePA payment.
            </p>
          </div>

          <button
            onClick={() => navigate('/karaoke')}
            className="brutal-btn-secondary w-full"
            data-testid="back-to-calendar-btn"
          >
            BACK TO CALENDAR
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmationPage;
